"use client";

import { useEffect, useState } from "react";

type TurnstileIssuedEvent = CustomEvent<{
  expiresAt?: number;
}>;

type TurnstileInvalidatedEvent = CustomEvent<{
  reason?: string;
}>;

export interface TurnstileSessionState {
  isReady: boolean;
  expiresAt: number | null;
  lastError: string | null;
}

export function useTurnstileSession(): TurnstileSessionState {
  const [isReady, setIsReady] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const handleIssued = (event: Event) => {
      const detail = (event as TurnstileIssuedEvent).detail;
      setIsReady(true);
      setExpiresAt(detail?.expiresAt ?? null);
      setLastError(null);
    };

    const handleInvalidated = (event: Event) => {
      const detail = (event as TurnstileInvalidatedEvent).detail;
      setIsReady(false);
      setExpiresAt(null);
      setLastError(detail?.reason ?? null);
    };

    document.addEventListener("turnstile:token:issued", handleIssued as EventListener);
    document.addEventListener(
      "turnstile:token:invalidated",
      handleInvalidated as EventListener,
    );

    return () => {
      document.removeEventListener("turnstile:token:issued", handleIssued as EventListener);
      document.removeEventListener(
        "turnstile:token:invalidated",
        handleInvalidated as EventListener,
      );
    };
  }, []);

  return { isReady, expiresAt, lastError };
}
